/**
 * Collect the environment signals server/middleware/vmGuard.js scores.
 *
 * Computed once and cached: reading the WebGL renderer allocates a GL context, which is
 * not something to do on every request.
 *
 * These signals are advisory. They are written by the page, so anyone who wants to lie can
 * — the guard is a policy control, not a security boundary. Nothing here reads personal
 * data: no fonts, no canvas fingerprint, no storage probing. Only the graphics driver
 * string, core count and screen geometry, which is the minimum the VM check needs.
 */

let cached = null;

function readRenderer() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return 'no-webgl';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    // UNMASKED_RENDERER_WEBGL names the actual driver ("VMware SVGA 3D"); without the
    // extension the plain RENDERER is a generic string and tells us nothing.
    const name = ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    const lose = gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
    return String(name || '');
  } catch {
    return '';
  }
}

export function getDeviceSignals() {
  if (cached) return cached;
  try {
    cached = {
      gpu: readRenderer(),
      cores: navigator.hardwareConcurrency || 0,
      dpr: window.devicePixelRatio || 1,
      screenW: window.screen?.width || 0,
      screenH: window.screen?.height || 0,
      touch: (navigator.maxTouchPoints || 0) > 0,
      headless: navigator.webdriver === true,
    };
  } catch {
    cached = {};
  }
  return cached;
}

/** Base64 of the signals, for the `x-client-env` header. */
export function deviceSignalsHeader() {
  try {
    const json = JSON.stringify(getDeviceSignals());
    // btoa is latin1-only; the renderer string can carry non-ASCII on some drivers.
    return btoa(unescape(encodeURIComponent(json)));
  } catch {
    return '';
  }
}
